import { useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

/** Section header + horizontally scrolling row with arrow buttons (keyboard and touch friendly). */
export function Rail({
  title,
  subtitle,
  seeAll,
  children,
  label,
}: {
  title: string;
  subtitle?: string;
  seeAll?: string;
  children: ReactNode;
  label?: string;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => scroller.current?.scrollBy({ left: dir * scroller.current.clientWidth * 0.85, behavior: "smooth" });
  return (
    <section className="container-rs" aria-label={label ?? title}>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold sm:text-2xl">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {seeAll && (
            <Link to={seeAll} className="mr-1 text-sm font-medium text-[var(--primary-text)] hover:underline">
              See all
            </Link>
          )}
          <button
            type="button"
            onClick={() => scroll(-1)}
            className="hidden size-9 place-items-center rounded-full border border-line text-ink-soft hover:border-[var(--border-glow)] hover:text-ink sm:grid"
            aria-label={`Scroll ${title} left`}
          >
            <ChevronLeft className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            className="hidden size-9 place-items-center rounded-full border border-line text-ink-soft hover:border-[var(--border-glow)] hover:text-ink sm:grid"
            aria-label={`Scroll ${title} right`}
          >
            <ChevronRight className="size-4" aria-hidden />
          </button>
        </div>
      </div>
      <div
        ref={scroller}
        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-4 pt-1 [scrollbar-width:none] sm:-mx-6 sm:scroll-px-6 sm:px-6 lg:-mx-8 lg:scroll-px-8 lg:px-8 [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
    </section>
  );
}

/** Fixed-width wrapper so cards line up in a rail. */
export function RailItem({ children }: { children: ReactNode }) {
  return <div className="w-[46%] shrink-0 snap-start xs:w-44 sm:w-52 lg:w-56">{children}</div>;
}
